export function formatDateTime(dateStr: string | undefined | null): string {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}`;
  } catch {
    return '-';
  }
}

export function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day}`;
  } catch {
    return '-';
  }
}

export function formatDateTimeWithSeconds(dateStr: string | undefined | null): string {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}:${bj.second}`;
  } catch {
    return '-';
  }
}

interface BeijingTimeParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

function extractBeijingTime(dateStr: string): BeijingTimeParts | null {
  // 处理带时区的ISO格式 (如 2026-05-18T12:19:52+08:00 或 2026-05-18T12:19:52Z)
  if (dateStr.includes('T') && (dateStr.includes('+') || dateStr.includes('Z'))) {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    // 已经是带时区的格式，直接转换为北京时间
    const bjDate = new Date(d.getTime() + 8 * 60 * 60 * 1000);
    return {
      year: String(bjDate.getUTCFullYear()),
      month: String(bjDate.getUTCMonth() + 1).padStart(2, '0'),
      day: String(bjDate.getUTCDate()).padStart(2, '0'),
      hour: String(bjDate.getUTCHours()).padStart(2, '0'),
      minute: String(bjDate.getUTCMinutes()).padStart(2, '0'),
      second: String(bjDate.getUTCSeconds()).padStart(2, '0'),
    };
  }
  // 处理PostgreSQL格式: 2026-05-18 12:19:52
  // 数据库存储的是UTC时间，需要+8小时转换为北京时间
  const match = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T ](\d{1,2}):(\d{1,2}):(\d{1,2})/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    const hour = parseInt(match[4], 10);
    const minute = parseInt(match[5], 10);
    const second = parseInt(match[6], 10);
    // 数据库存储的是UTC时间，加8小时转换为北京时间
    const bjHour = hour + 8;
    const bjDay = bjHour >= 24 ? day + 1 : day;
    const bjHourFinal = bjHour >= 24 ? bjHour - 24 : bjHour;
    // 处理跨月的情况
    let bjMonth = month;
    let bjYear = year;
    if (bjDay > 31) {
      // 简单处理，实际应该根据月份计算天数
      bjMonth = month + 1;
      if (bjMonth > 12) {
        bjYear = year + 1;
        bjMonth = 1;
      }
    }
    return {
      year: String(bjYear),
      month: String(bjMonth).padStart(2, '0'),
      day: String(bjDay > 31 ? 1 : bjDay).padStart(2, '0'),
      hour: String(bjHourFinal).padStart(2, '0'),
      minute: String(minute).padStart(2, '0'),
      second: String(second).padStart(2, '0'),
    };
  }
  const dateOnly = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (dateOnly) {
    return {
      year: dateOnly[1],
      month: dateOnly[2].padStart(2, '0'),
      day: dateOnly[3].padStart(2, '0'),
      hour: '00',
      minute: '00',
      second: '00',
    };
  }
  return null;
}

export function toLocalDateString(date: Date): string {
  const bjDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
  return `${bjDate.getUTCFullYear()}-${String(bjDate.getUTCMonth() + 1).padStart(2, '0')}-${String(bjDate.getUTCDate()).padStart(2, '0')}`;
}

export function toLocalDateTimeString(date: Date): string {
  const bjDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
  return `${bjDate.getUTCFullYear()}-${String(bjDate.getUTCMonth() + 1).padStart(2, '0')}-${String(bjDate.getUTCDate()).padStart(2, '0')}T${String(bjDate.getUTCHours()).padStart(2, '0')}:${String(bjDate.getUTCMinutes()).padStart(2, '0')}`;
}

export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0秒';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}小时${minutes}分${secs}秒`;
  } else if (minutes > 0) {
    return `${minutes}分${secs}秒`;
  } else {
    return `${secs}秒`;
  }
}